import {
    Avatar,
    componentStyles,
    Menu,
    SIZES,
} from "@mallardbay/lib-react-components"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown"
import { faEarthAsia } from "@fortawesome/free-solid-svg-icons/faEarthAsia"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React from "react"

import { usePortalEntitiesContext } from "~contexts/outfitter-provider"
import { PortalType } from "~graphql/generated/types"
import type { MinimalOutfitterFieldsFragment } from "~graphql/outfitters.generated"
import type { EventOrganizerFieldsFragment } from "~graphql/event-organizers.generated"
import {
    useAdminSearchProps,
    useSelectedEntity,
} from "~components/shared/portal-entity-selector/portal-entity-selector-helpers"
import { useUser } from "~contexts/user-provider"
import { TEST_IDS } from "~config/constants"
import SelectedEntity from "~components/shared/portal-entity-selector/selected-entity"
import COPY from "~config/copy-constants"

interface Props {
    readonly shouldRenderSelectedEntity?: boolean
}

export default function PortalEntitySelector({
    shouldRenderSelectedEntity = true,
}: Props) {
    const styles = useStyles()

    const { isAdminNotImpersonating } = useUser()
    const {
        portalType,
        outfitter: selectedOutfitter,
        eventOrganizer: selectedEventOrganizer,
        isLoading,
    } = usePortalEntitiesContext()
    const selectedEntity = useSelectedEntity({
        portalType,
        selectedOutfitter,
        selectedEventOrganizer,
    })
    const menuItems = useMenuItems({ isAdminNotImpersonating })
    const menuSearchProps = useAdminSearchProps()
    const shouldRender = menuItems.length > 1 || isAdminNotImpersonating

    if (!shouldRender && !isLoading) return null

    return (
        <Menu
            testId={TEST_IDS.PORTAL_ENTITY_SELECTOR}
            style={styles.root}
            menuItems={menuItems}
            menuSearchProps={menuSearchProps}
            isLoading={isLoading && !selectedEntity}
            rightIcon={faChevronDown}
        >
            <SelectedEntity
                portalType={portalType}
                selectedEntity={selectedEntity}
                shouldRender={shouldRenderSelectedEntity}
            />
        </Menu>
    )
}

function useStyles() {
    return componentStyles({
        root: {
            width: "100%",
        },
        avatar: {
            width: "20px",
            height: "20px",
        },
    })
}

function useMenuItems({
    isAdminNotImpersonating,
}: {
    isAdminNotImpersonating: boolean
}) {
    const {
        portalType,
        outfitters,
        eventOrganizers,
        setOutfitter,
        setEventOrganizer,
    } = usePortalEntitiesContext()
    const outfitterMenuItems = useOutfitterMenuItems({
        outfitters,
        setOutfitter,
        isAdminNotImpersonating,
    })
    const eventOrganizerMenuItems = useEventOrganizerMenuItems({
        eventOrganizers,
        setEventOrganizer,
        isAdminNotImpersonating,
    })

    switch (portalType) {
        case PortalType.Outfitter:
            return outfitterMenuItems
        case PortalType.RegionalDirector:
            return eventOrganizerMenuItems
        default:
            return []
    }
}

function useOutfitterMenuItems({
    outfitters,
    setOutfitter,
    isAdminNotImpersonating,
}: {
    outfitters: MinimalOutfitterFieldsFragment[]
    setOutfitter: (outfitter: MinimalOutfitterFieldsFragment | null) => void
    isAdminNotImpersonating: boolean
}) {
    const styles = useStyles()

    const allOutfittersMenuItem = {
        label: COPY.ALL_OUTFITTERS,
        onClick: () => setOutfitter(null),
        icon: <FontAwesomeIcon icon={faEarthAsia} style={styles.avatar} />,
    }
    const outfitterMenuItems = outfitters.map((outfitter) => ({
        label: outfitter.name,
        onClick: () => setOutfitter(outfitter),
        icon: (
            <Avatar
                src={outfitter.avatar?.url}
                name={outfitter.name}
                size={SIZES.XS}
                style={styles.avatar}
            />
        ),
    }))

    return isAdminNotImpersonating
        ? [allOutfittersMenuItem, ...outfitterMenuItems]
        : outfitterMenuItems
}

function useEventOrganizerMenuItems({
    eventOrganizers,
    setEventOrganizer,
    isAdminNotImpersonating,
}: {
    eventOrganizers: EventOrganizerFieldsFragment[]
    setEventOrganizer: (
        eventOrganizer: EventOrganizerFieldsFragment | null
    ) => void
    isAdminNotImpersonating: boolean
}) {
    const styles = useStyles()

    const allEventOrganizersMenuItem = {
        label: COPY.ALL_REGIONAL_DIRECTORS,
        onClick: () => setEventOrganizer(null),
        icon: <FontAwesomeIcon icon={faEarthAsia} style={styles.avatar} />,
    }
    const eventOrganizerMenuItems = eventOrganizers.map((eventOrganizer) => ({
        label: eventOrganizer.name,
        onClick: () => setEventOrganizer(eventOrganizer),
        icon: (
            <Avatar
                style={styles.avatar}
                name={eventOrganizer.name}
                size={SIZES.XS}
            />
        ),
    }))

    return isAdminNotImpersonating
        ? [allEventOrganizersMenuItem, ...eventOrganizerMenuItems]
        : eventOrganizerMenuItems
}
